import { useQuery } from '@tanstack/react-query';
import { ArrowRightIcon } from 'lucide-react';
import { Link } from 'react-router';

import { Avatar, AvatarFallback } from '@/components/core/avatar';
import { Button } from '@/components/core/button';
import { Card, CardContent } from '@/components/core/card';
import { Skeleton } from '@/components/core/skeleton';
import { useGateways } from '@/gateways/GatewayProvider';
import { carrierInitials } from '@/pages/platform/carriers/carrier-initials';
import { fetchAllPages } from '@/shared/helpers';

type CarrierSummaryCardProps = {
  carrierId?: string;
  carrierName?: string;
  baseTrackingUrl?: string | null;
  isLoading?: boolean;
};

/**
 * Carrier identity summary shown at the top of the carrier-account detail page. The carrier has no
 * logo field (`CarrierDTO`), so it uses a letter-avatar like the list. Identity comes from the
 * parent (name/id + tracking URL); the service count is resolved here from the carrier's services.
 */
export function CarrierSummaryCard({
  carrierId,
  carrierName,
  baseTrackingUrl,
  isLoading,
}: CarrierSummaryCardProps) {
  const { shippingGateway } = useGateways();
  const { data: carrierServices } = useQuery({
    queryKey: ['carrierServices'],
    queryFn: ({ signal }) =>
      fetchAllPages(({ cursor, take }) =>
        shippingGateway.getCarrierServices({ cursor, take }, { signal }),
      ),
    enabled: !!carrierId,
  });
  const serviceCount =
    carrierServices && carrierId
      ? carrierServices.filter((service) => service.carrierId === carrierId).length
      : undefined;

  if (isLoading || !carrierName) {
    return (
      <Card>
        <CardContent className="flex items-center gap-3">
          <Skeleton className="size-10 rounded-full" />
          <div className="flex flex-col gap-2">
            <Skeleton className="h-4 w-40" />
            <Skeleton className="h-3 w-56" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasUrl = !!baseTrackingUrl;
  const hasCount = serviceCount !== undefined;

  return (
    <Card>
      <CardContent className="flex items-center gap-3">
        <Avatar size="lg">
          <AvatarFallback>{carrierInitials(carrierName)}</AvatarFallback>
        </Avatar>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-semibold">{carrierName}</div>
          {(hasUrl || hasCount) && (
            <div className="flex min-w-0 items-center gap-x-1.5 text-sm text-muted-foreground">
              {hasUrl && (
                <span className="min-w-0 truncate font-mono text-xs">{baseTrackingUrl}</span>
              )}
              {hasUrl && hasCount && <span aria-hidden>·</span>}
              {hasCount && (
                <span className="shrink-0">
                  {serviceCount} {serviceCount === 1 ? 'service' : 'services'}
                </span>
              )}
            </div>
          )}
        </div>
        {carrierId && (
          <Button asChild variant="outline" size="sm">
            <Link to={`/shipping/carriers/${carrierId}`}>
              Go to carrier
              <ArrowRightIcon />
            </Link>
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
